import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Loader2, Users, Search, X } from 'lucide-react';
import PublicLayout from '../../components/public/PublicLayout';
import ArtistCard from '../../components/public/ArtistCard';
import SEO from '../../components/SEO';

const LIMIT = 40;

function PublicArtistListPage() {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [selectedGenre, setSelectedGenre] = useState(null);
  const [genres, setGenres] = useState([]);

  const debounceRef = useRef(null);

  const fetchArtists = useCallback(async (p, q, genre, append = false) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const params = { page: p, limit: LIMIT, search: q };
      if (genre) params.genre = genre;
      const res = await axios.get('/public/artists', { params });
      const items = res.data.items || [];
      setArtists((prev) => {
        if (append) {
          const existingIds = new Set(prev.map((a) => a.id));
          return [...prev, ...items.filter((a) => !existingIds.has(a.id))];
        }
        return items;
      });
      setPage(res.data.currentPage || p);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      console.error('아티스트 목록 로드 실패:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    axios.get('/public/artist-genres').then(res => setGenres(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    fetchArtists(1, search, selectedGenre);
  }, [search, selectedGenre]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearch(val), 400);
  };

  const clearSearch = () => {
    setInputValue('');
    setSearch('');
  };

  const handleLoadMore = () => {
    if (page < totalPages && !loadingMore) {
      fetchArtists(page + 1, search, selectedGenre, true);
    }
  };

  const selectGenre = (genre) => {
    setSelectedGenre(prev => prev === genre ? null : genre);
  };

  return (
    <PublicLayout>
      <SEO
        title="아티스트"
        description="p!nk 소속 아티스트를 만나보세요."
        path="/artists"
      />
      {/* 헤더 */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 pb-4">
        <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight text-center">아티스트</h1>
      </div>

      {/* 장르 필터 탭 */}
      {genres.length > 0 && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-3">
          <div className="overflow-x-auto scroll-dark">
          <div className="flex gap-2 pb-1 w-max mx-auto">
            <button
              onClick={() => setSelectedGenre(null)}
              className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                !selectedGenre
                  ? 'bg-sky-500 border-sky-500 text-white'
                  : 'bg-white/5 border-white/10 text-slate-400 hover:text-white hover:border-white/20'
              }`}>
              전체
            </button>
            {genres.map(g => (
              <button
                key={g.genre}
                onClick={() => selectGenre(g.genre)}
                className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  selectedGenre === g.genre
                    ? 'bg-sky-500 border-sky-500 text-white'
                    : 'bg-white/5 border-white/10 text-slate-400 hover:text-white hover:border-white/20'
                }`}>
                {g.genre}
              </button>
            ))}
          </div>
          </div>
        </div>
      )}

      {/* 검색 */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-4">
        <div className="flex items-center sm:justify-end justify-center">
          <div className="relative w-full sm:w-72">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
            <input
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              placeholder="아티스트 검색"
              className="w-full pl-8 pr-8 py-2 bg-white/5 border border-white/10 rounded-full text-sm text-white placeholder-slate-500 focus:outline-none focus:border-sky-500/50 transition-colors"
            />
            {inputValue && (
              <button onClick={clearSearch} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
                <X size={13} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 아티스트 그리드 */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-10">
        {loading ? (
          <div className="flex items-center justify-center h-60 gap-3 text-slate-400">
            <Loader2 size={22} className="animate-spin" />
            <span>로딩 중...</span>
          </div>
        ) : artists.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-slate-500">
            <Users size={40} />
            <p>{search ? '검색 결과가 없습니다.' : '아티스트가 없습니다.'}</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 sm:gap-3">
              {artists.map((artist) => (
                <ArtistCard key={artist.id} artist={artist} />
              ))}
              {Array.from({ length: 7 }).map((_, i) => (
                <div key={`pad-${i}`} aria-hidden="true" />
              ))}
            </div>

            {page < totalPages && (
              <div className="flex justify-center mt-10">
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="flex items-center gap-2 px-6 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-slate-300 text-sm transition-colors">
                  {loadingMore ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      로딩 중...
                    </>
                  ) : (
                    '더 보기'
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </PublicLayout>
  );
}


export default PublicArtistListPage;
